import { produce } from "../../../../immer/index.js";
import { throwIfInvalidArrayFieldIndex } from "../../utils/throwIfInvalidArrayFieldIndex.js";
import { throwIfNotArrayField } from "../../utils/throwIfNotArrayField.js";
function createArrayFieldApi(store, getFormApi, getLogger, getPrivateFieldApi) {
  const { tryToCreateField } = getPrivateFieldApi();
  return {
    /**
     * Changes the value of an element in an array field.
     *
     * @param fieldName The name of the target field (should be an array field).
     * @param index The index of the element to update inside the array.
     * @param maybeLazyValue New value or update function.
     */
    changeArrayElement: (fieldName, index, maybeLazyValue) => {
      const log = getLogger();
      const formApi = getFormApi();
      tryToCreateField(fieldName, []);
      throwIfNotArrayField(store, fieldName);
      const value = store.fields[fieldName]?.value;
      throwIfInvalidArrayFieldIndex(value, index);
      log("changeArrayElement", fieldName, index, maybeLazyValue);
      const newValue = produce(value, (draft) => {
        draft[index] = typeof maybeLazyValue === "function" ? maybeLazyValue(
          draft[index]
        ) : maybeLazyValue;
      });
      const operations = [{ type: "change", index }];
      formApi.change(fieldName, newValue, operations);
    },
    /**
     * Removes an Element of an array field from a specified position.
     *
     * @param fieldName The name of the target field (should be an array field).
     * @param index The index of the value to remove.
     */
    removeArrayElements: (fieldName, index) => {
      const log = getLogger();
      const formApi = getFormApi();
      tryToCreateField(fieldName, []);
      throwIfNotArrayField(store, fieldName);
      const value = store.fields[fieldName]?.value;
      throwIfInvalidArrayFieldIndex(value, index);
      log("removeArrayElements", fieldName, index);
      const sortedIndexes = Array.isArray(index) ? index.concat().sort().reverse() : [index];
      const newValue = produce(value, (draft) => {
        for (const i of sortedIndexes) {
          draft.splice(i, 1);
        }
      });
      const operations = [{ type: "remove", index: Array.isArray(index) ? index : [index] }];
      formApi.change(fieldName, newValue, operations);
    },
    /**
     * Adds one or more elements in a specified position of the array field.
     *
     * @param fieldName The name of the target field (should be an array field).
     * @param index Index of the position where to add the Element.
     * @param elements The elements to add into the array.
     */
    addArrayElements: (fieldName, index, ...elements) => {
      const log = getLogger();
      const formApi = getFormApi();
      tryToCreateField(fieldName, []);
      throwIfNotArrayField(store, fieldName);
      const value = store.fields[fieldName]?.value;
      if (index !== "end") {
        throwIfInvalidArrayFieldIndex(value, index);
      }
      log("addArrayElements", fieldName, index);
      const newValue = produce(value, (draft) => {
        if (index === "end") {
          draft.push(...elements);
        } else {
          draft.splice(index, 0, ...elements);
        }
      });
      const start = index === "end" ? value.length : index;
      const operations = [
        {
          type: "add",
          index: Array.from({ length: elements.length }, (_, i) => start + i)
        }
      ];
      formApi.change(fieldName, newValue, operations);
    },
    /**
     * Swap the position of two elements inside an array field.
     *
     * @param fieldName The name of the target field (should be an array field).
     * @param indexA The index of the first element to swap
     * @param indexB The index of the second element to swap
     */
    swapArrayElements: (fieldName, indexA, indexB) => {
      const log = getLogger();
      const formApi = getFormApi();
      tryToCreateField(fieldName, []);
      throwIfNotArrayField(store, fieldName);
      const value = store.fields[fieldName]?.value;
      throwIfInvalidArrayFieldIndex(store.fields[fieldName]?.value, indexA);
      throwIfInvalidArrayFieldIndex(store.fields[fieldName]?.value, indexB);
      log("swapArrayElements", fieldName, indexA, indexB);
      const newValue = produce(value, (draft) => {
        const temp = structuredClone(draft[indexA]);
        draft[indexA] = draft[indexB];
        draft[indexB] = temp;
      });
      const operations = [{ type: "swap", index: [indexA, indexB] }];
      formApi.change(fieldName, newValue, operations);
    },
    /**
     * Clears all the elements of an array field.
     *
     * @param fieldName The name of the target field (should be an array field).
     */
    clearArrayElements: (fieldName) => {
      const log = getLogger();
      const formApi = getFormApi();
      tryToCreateField(fieldName, []);
      throwIfNotArrayField(store, fieldName);
      const value = store.fields[fieldName]?.value;
      log("clearArrayElements", fieldName);
      const operations = [{ type: "remove", index: [...value.keys()] }];
      formApi.change(fieldName, [], operations);
    }
  };
}
export {
  createArrayFieldApi
};
//# sourceMappingURL=createArrayFieldApi.js.map
